import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { cn } from 'src/lib/utils';

export type EtlTableColumn<T> = {
  id: string;
  header: string;
  accessor: (row: T) => string | number | null | undefined;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right';
};

type FilterConfig<T> = {
  id: string;
  label: string;
  options: string[];
  getValue: (row: T) => string | null | undefined;
};

type SortState = {
  columnId: string;
  direction: 'asc' | 'desc';
};

type Props<T> = {
  rows: T[];
  columns: EtlTableColumn<T>[];
  filters?: FilterConfig<T>[];
  emptyMessage?: string;
};

function compareValues(
  left: string | number | null | undefined,
  right: string | number | null | undefined,
): number {
  if (left === right) {
    return 0;
  }
  if (left === null || left === undefined || left === '') {
    return 1;
  }
  if (right === null || right === undefined || right === '') {
    return -1;
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  return String(left).localeCompare(String(right));
}

export function EtlDetailTable<T>({
  rows,
  columns,
  filters = [],
  emptyMessage = 'No rows match the current filters.',
}: Props<T>) {
  const [sort, setSort] = useState<SortState | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const filteredRows = useMemo(() => {
    return rows.filter((row) => filters.every((filter) => {
      const selected = filterValues[filter.id];
      if (!selected || selected === 'all') {
        return true;
      }
      return (filter.getValue(row) ?? '').toLowerCase() === selected.toLowerCase();
    }));
  }, [rows, filters, filterValues]);

  const sortedRows = useMemo(() => {
    if (!sort) {
      return filteredRows;
    }
    const column = columns.find((entry) => entry.id === sort.columnId);
    if (!column) {
      return filteredRows;
    }
    return [...filteredRows].sort((left, right) => {
      const result = compareValues(column.accessor(left), column.accessor(right));
      return sort.direction === 'asc' ? result : -result;
    });
  }, [columns, filteredRows, sort]);

  const toggleSort = (columnId: string) => {
    setSort((current) => {
      if (!current || current.columnId !== columnId) {
        return { columnId, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { columnId, direction: 'desc' };
      }
      return null;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {filters.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {filters.map((filter) => (
            <label key={filter.id} className="flex flex-col gap-1 text-xs text-gray-600">
              <span className="font-medium">{filter.label}</span>
              <select
                className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900"
                value={filterValues[filter.id] ?? 'all'}
                onChange={(event) => {
                  setFilterValues((current) => ({
                    ...current,
                    [filter.id]: event.target.value,
                  }));
                }}
              >
                <option value="all">All</option>
                {filter.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => {
                const isActive = sort?.columnId === column.id;
                const SortIcon = !column.sortable
                  ? null
                  : !isActive
                    ? ArrowUpDown
                    : sort?.direction === 'asc'
                      ? ArrowUp
                      : ArrowDown;

                return (
                  <TableHead
                    key={column.id}
                    className={cn(column.align === 'right' && 'text-right')}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
                        onClick={() => toggleSort(column.id)}
                      >
                        {column.header}
                        {SortIcon ? <SortIcon className="size-3.5" /> : null}
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-8 text-center text-sm text-gray-600">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row, index) => (
                <TableRow key={index}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(column.align === 'right' && 'text-right font-mono text-xs')}
                    >
                      {column.render ? column.render(row) : column.accessor(row) ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-gray-500">
        Showing
        {' '}
        {sortedRows.length}
        {' '}
        of
        {' '}
        {rows.length}
        {' '}
        rows
      </p>
    </div>
  );
}
